import React, { createRef, useEffect, useState } from 'react';
import { IMessageEditorProps } from "./~types/MessageEditorProps";
import { Button } from 'reactstrap';
import { DELETED_MESSAGE_MESSAGE, CANNOT_EDIT_MESSAGE_WITH_FILE } from './message-editor-constants/MessageEditorConstants';
import { showErrorToastr } from "src/app/shared/helpers/toastr-helper/ToastrHelper";
import Input from "@mui/material/Input";

import './MessageEditor.css';
import { useTranslation } from 'react-i18next';
import { TopicPairContentType } from 'src/app/nlp/~store/models/enums/TopicPairContentType';
import { INPUT_SYMBOLS_COUNT, WECHAT_INPUT_SYMBOLS_COUNT } from "../ChatConstants";
import { ChannelType } from "src/app/contact/~store/models/ChannelType";

const MessageEditor = (props: IMessageEditorProps) => {
    const { t } = useTranslation();
    const [editedText, setEditedText] = useState(props.messageToEdit.text);
    const [isFocused, setIsFocued] = useState(false);
    const messageEditorRef = createRef<HTMLTextAreaElement>();
    const NEW_LINE = "\n";

    useEffect(() => {
        if(messageEditorRef.current && !isFocused) {
            messageEditorRef.current.focus();
            setIsFocued(true);
        }
    });

    useEffect(() => {
        props.onChangeHeight();
    });

    const onCloseHandler = () => {
        setIsFocued(false);
        props.onClose?.();
    }

    const handleCloseButtonKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
            onCloseHandler();
        }
    }

    const isNotEmptyEditMessage = (): boolean => {
        if (editedText.trim().length === 0) {
            showErrorToastr(t("chat.cannotSaveEmptyMessaage"))
            return false;
        }        
        return true;
    }

    const getInputSymbolMaxCount = (): number => {
        return props.selectedChat?.channel.type === ChannelType.WeChat
            ? WECHAT_INPUT_SYMBOLS_COUNT
            : INPUT_SYMBOLS_COUNT;
    }

    return (
        <div
            className="message-editor__container"
            id="message-editor__container"
            onKeyDown={(event) => {
                if (!event.shiftKey && event.key === "Enter") {
                    var message = { ...props.messageToEdit };
                    if (!message.text && (message.imageId || message.fileId)) {
                        showErrorToastr(t("chat.messageFileNoEdit"));
                    }
                    else if (!isNotEmptyEditMessage()) {
                        return;
                    }
                    else {
                        props.onEdit({
                            ...props.messageToEdit,
                            text: editedText,
                        });
                    }
                    onCloseHandler();
                }
            }}
        >
            <div className="message-to-edit">
                <div className="message-to-edit__header">
                    {t("chat.editMessage")}
                    <div
                        className="message-to-edit__close-editor"
                        onClick={onCloseHandler}
                        tabIndex={0}
                        onKeyUp={handleCloseButtonKeyUp}
                    >
                        <i className="material-icons">close</i>
                    </div>
                </div>
                <textarea
                    readOnly
                    className="message-to-edit__text"
                    id="message-to-edit__text"
                >
                    {props.messageToEdit.text}
                </textarea>
                <div className="message-to-edit__footer"></div>
            </div>
            <div className="message-editor">
                <Input
                    type="text"
                    className="message-editor__input"
                    id="message-editor__input"
                    value={editedText}
                    inputRef={messageEditorRef}
                    multiline={true}
                    maxRows={props.rowsMax}
                    inputProps={{
                        maxLength: getInputSymbolMaxCount(),
                    }}
                    onFocus={(event) => {
                        const text = event.currentTarget.value;
                        event.currentTarget.setSelectionRange(
                            text.length,
                            text.length
                        );
                        event.currentTarget.scrollTop =
                            event.currentTarget.scrollHeight;
                    }}
                    onChange={(event) =>  {
                        if (event.target.value !== NEW_LINE) 
                            setEditedText(event.target.value)
                    }}
                />
                <div className="edit-message-buttons">
                    <Button
                        onClick={() => {
                            var message = { ...props.messageToEdit };
                            if (
                                !message.text &&
                                (message.imageId || message.fileId)
                            ) {
                                showErrorToastr(t("chat.messageFileNoEdit"));
                            }
                            else if (!isNotEmptyEditMessage()) {
                                return;
                            } 
                            else {
                                props.onEdit({
                                    ...props.messageToEdit,
                                    text: editedText,
                                    imageId: props.messageToEdit.imageId,
                                    fileId: props.messageToEdit.fileId,
                                });
                            }
                            onCloseHandler();
                        }}
                    >
                        <i className="material-icons">done</i>
                    </Button>
                    <Button
                        className="message-editor__remove-message"
                        onClick={
                            () => {
                                if (props.messageToEdit) {
                                    props.onDelete(props.messageToEdit.messageId);
                                }
                                onCloseHandler();
                            }
                        }>
                        <i className="material-icons">
                            delete_forever
                        </i>
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default MessageEditor;